/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Logger } from '../services/logger';

export type Currency = 'EUR' | 'CHF' | 'GBP' | 'USD';

interface ExchangeRates {
    EUR: number;
    CHF: number;
    GBP: number;
    USD: number;
    lastUpdated: number;
}

// Cache exchange rates for 24 hours
let cachedRates: ExchangeRates | null = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Cache detected currency from IP for session
let cachedIpCurrency: Currency | null = null;

/** Map ISO country code to currency */
function countryCurrencyMap(country: string): Currency | null {
    const ch = ['CH', 'LI'];
    const gb = ['GB', 'IM', 'JE', 'GG'];
    const eu = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'IE', 'FI', 'GR', 'LU', 'SI', 'SK', 'EE', 'LV', 'LT', 'CY', 'MT'];
    if (ch.includes(country)) return 'CHF';
    if (gb.includes(country)) return 'GBP';
    if (eu.includes(country)) return 'EUR';
    return null;
}

/**
 * Detect user's currency based on browser locale (synchronous fallback)
 */
export function detectUserCurrency(): Currency {
    if (cachedIpCurrency) return cachedIpCurrency;

    const locale = navigator.language || 'en-US';
    const region = locale.split('-')[1]?.toUpperCase();
    if (region) {
        const mapped = countryCurrencyMap(region);
        if (mapped) return mapped;
    }
    return 'USD';
}

/**
 * Detect currency via IP geolocation (async, cached per session).
 * Falls back to locale-based detection if the API is unavailable.
 */
export async function detectUserCurrencyAsync(): Promise<Currency> {
    if (cachedIpCurrency) return cachedIpCurrency;

    try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
            const data = await res.json();
            const country: string = (data.country_code || '').toUpperCase();
            const fromCountry = countryCurrencyMap(country);
            // Also try currency field returned by ipapi
            const fromApi = (data.currency || '') as Currency;
            const validCurrencies: Currency[] = ['EUR', 'CHF', 'GBP', 'USD'];
            const detected = fromCountry || (validCurrencies.includes(fromApi) ? fromApi : null) || 'USD';
            cachedIpCurrency = detected;
            return detected;
        }
    } catch {
        // ignore – fall through to locale detection
    }
    const fallback = detectUserCurrency();
    cachedIpCurrency = fallback;
    return fallback;
}

/**
 * Fetch current exchange rates from an API
 * Falls back to static rates if API fails
 */
async function fetchExchangeRates(): Promise<ExchangeRates> {
    // Check cache first
    if (cachedRates && (Date.now() - cachedRates.lastUpdated) < CACHE_DURATION) {
        return cachedRates;
    }

    try {
        // Using exchangerate-api.com (free tier available)
        // Alternative: xe.com doesn't have a free public API
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');

        if (!response.ok) {
            throw new Error('Exchange rate API failed');
        }

        const data = await response.json();

        cachedRates = {
            EUR: 1,
            CHF: data.rates.CHF || 0.95,
            GBP: data.rates.GBP || 0.85,
            USD: data.rates.USD || 1.10,
            lastUpdated: Date.now()
        };

        Logger.info('Exchange rates updated', cachedRates);
        return cachedRates;
    } catch (error) {
        Logger.warn('Failed to fetch exchange rates, using fallback', error);

        // Fallback to approximate static rates
        return {
            EUR: 1,
            CHF: 0.95,
            GBP: 0.85,
            USD: 1.10,
            lastUpdated: Date.now()
        };
    }
}

/**
 * Convert price from EUR base to target currency
 */
export async function convertPrice(amountEUR: number, targetCurrency: Currency): Promise<number> {
    if (targetCurrency === 'EUR') {
        return amountEUR;
    }

    const rates = await fetchExchangeRates();
    return amountEUR * rates[targetCurrency];
}

/**
 * Format price with currency symbol and proper locale formatting
 */
export function formatPrice(amount: number, currency: Currency, locale?: string): string {
    const detectedLocale = locale || navigator.language || 'en-US';

    try {
        return new Intl.NumberFormat(detectedLocale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    } catch (error) {
        // Fallback formatting if Intl fails
        const symbols: Record<Currency, string> = {
            EUR: '€',
            CHF: 'Fr.',
            GBP: '£',
            USD: '$'
        };

        return `${symbols[currency]}${amount.toFixed(2)}`;
    }
}

/**
 * Get localized price string for display
 * Converts from EUR base amount and formats for user's currency
 */
export async function getLocalizedPrice(
    baseAmountEUR: number,
    userCurrency?: Currency
): Promise<string> {
    const currency = userCurrency || detectUserCurrency();
    const convertedAmount = await convertPrice(baseAmountEUR, currency);
    return formatPrice(convertedAmount, currency);
}

/**
 * Hook to use localized pricing in components.
 * Starts with locale-based currency detection and upgrades to IP geolocation asynchronously.
 */
export function useLocalizedPrice() {
    const [currency, setCurrency] = React.useState<Currency>(detectUserCurrency);

    React.useEffect(() => {
        let cancelled = false;
        detectUserCurrencyAsync().then(detected => {
            if (!cancelled) setCurrency(detected);
        });
        return () => { cancelled = true; };
    }, []);

    return {
        currency,
        formatPrice: (amount: number) => formatPrice(amount, currency),
        convertAndFormat: async (baseAmountEUR: number) => {
            const converted = await convertPrice(baseAmountEUR, currency);
            return formatPrice(converted, currency);
        }
    };
}
